import { TransactionDetails } from '../../../payment.types.js';
import { TransactionDetailsTypeId } from '../../transaction-details.enums.js';
import type { BaseIpwTransactionDetails } from '../../../payment.types.js';
import { DealertrackScreenIdSchema } from './dealertrack-screen-id.js';

export type DealertrackDocumentTypeValues = readonly ['parts', 'service', 'deal'];

export const DealertrackDocumentTypeValues: DealertrackDocumentTypeValues = [
    'parts',
    'service',
    'deal',
];

export type DealertrackDocumentType = DealertrackDocumentTypeValues[number];

export type DealertrackTransactionDetailsTypeId =
    | typeof TransactionDetailsTypeId.DEALERTRACK_PARTS
    | typeof TransactionDetailsTypeId.DEALERTRACK_SERVICE
    | typeof TransactionDetailsTypeId.DEALERTRACK_DEAL
    | typeof TransactionDetailsTypeId.DEALERTRACK_LEASING
    | typeof TransactionDetailsTypeId.DEALERTRACK_AR_STATEMENT_CUSTOMERS;

export type DealertrackTransactionDetailsTypeVersion = 1;

export type DealertrackTransactionDetailsExtensionDiscriminant =
    DealertrackTransactionDetailsTypeId;

export type DealertrackPartsLineItem = {
    optionCode?: string;
    partNumber?: string;
    description?: string;
    binShelf?: string;
    quantity?: number;
    available?: number;
    listPrice?: number;
    netPrice?: number;
    extendedPrice?: number;
};

export type DealertrackPartsTotals = {
    freight?: number;
    fees?: number;
    partsSubtotal?: number;
    taxes?: number;
    deposit?: number;
    total?: number;
};

export type DealertrackPartsTransactionExtension = {
    partsInvoiceNumber?: string;
    closeDate?: string;
    paymentMethod?: string;
    controlReference?: string;
    accountCardLast4?: string;
    counterperson?: string;
    status?: string;
    transactionType?: string;
    customerAccountNumber?: string;
    mainPhone?: string;
    poNumber?: string;
    priceLevel?: string;
    saleType?: string;
    totals?: DealertrackPartsTotals;
    lineItems?: DealertrackPartsLineItem[];
};

export type DealertrackServiceTotalsByPillar = {
    labor?: number;
    parts?: number;
    deductible?: number;
    sublet?: number;
    shopSupplies?: number;
    paintMaterials?: number;
    miscCharges?: number;
    totalCharges?: number;
    salesTax?: number;
    totalDue?: number;
};

export type DealertrackServiceBillingTotals = {
    customerPay?: DealertrackServiceTotalsByPillar;
    warranty?: DealertrackServiceTotalsByPillar;
    internal?: DealertrackServiceTotalsByPillar;
    serviceContract?: DealertrackServiceTotalsByPillar;
};

export type DealertrackServiceTransactionExtension = {
    closeDate?: string;
    paymentMethod?: string;
    controlReference?: string;
    accountCardLast4?: string;
    roNumber?: string;
    status?: string;
    vehicleDescription?: string;
    vin?: string;
    tagNumber?: string;
    mileage?: number;
    serviceWriter?: string;
    dateOpened?: string;
    serviceGroup?: string;
    mainPhone?: string;
    billingTotalsByPillar?: DealertrackServiceBillingTotals;
};

export type DealertrackDealTransactionExtension = {
    homePhone?: string;
    cellPhone?: string;
    email?: string;
    county?: string;
    stockNumber?: string;
    vehicleDescription?: string;
    vin?: string;
    odometer?: number;
    tradeDescription?: string;
    msrp?: number;
    price?: number;
    downPayment?: number;
    tradeAllowance?: number;
    payoff?: number;
    rebate?: number;
    apr?: number;
    termMonths?: number;
    paymentAmount?: number;
    paymentFrequency?: string;
    saleDate?: string;
    deliverDate?: string;
    firstPaymentDate?: string;
    amountOptions?: number;
    serviceContractAmount?: number;
    insuranceAmount?: number;
    accessoriesAmount?: number;
    lender?: string;
    dealTax?: number;
    taxGroup?: string;
    salespersonPrimary?: string;
    salespersonSecondary?: string;
    financeManager?: string;
    salesManager?: string;
};

export type DealertrackLeasingTransactionExtension = {
    homePhone?: string;
    cellPhone?: string;
    email?: string;
    county?: string;
    stockNumber?: string;
    dealStatus?: string;
    vehicleDescription?: string;
    vin?: string;
    odometer?: number;
    tradeDescription?: string;
    msrp?: number;
    price?: number;
    tradeAllowance?: number;
    payoff?: number;
    rebate?: number;
    residualPercentage?: number;
    apr?: number;
    moneyFactor?: number;
    termMonths?: number;
    cashReceived?: number;
    upfrontAmount?: number;
    cashCapReduction?: number;
    saleDate?: string;
    deliverDate?: string;
    secondPaymentDate?: string;
    amountOptions?: number;
    insuranceAmount?: number;
    capReductionAmount?: number;
    accessoriesAmount?: number;
    serviceContractAmount?: number;
    lessor?: string;
    acquisitionFee?: number;
    leaseTax?: number;
    cashRequired?: number;
    taxGroup?: string;
    securityDeposit?: number;
    feeAmount?: number;
    functionCode?: string;
    permitNumberExpiration?: string;
    annualMiles?: number;
    pdi?: string;
    profitCode?: string;
    salespersonPrimary?: string;
    salespersonSecondary?: string;
    financeManager?: string;
    salesManager?: string;
    paymentAmount?: number;
    paymentFrequency?: string;
};

export type DealertrackPartsTransactionDetails = BaseIpwTransactionDetails &
    DealertrackPartsTransactionExtension & {
        transaction_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_PARTS;
        transaction_details_type_version: 1;
        dealertrack_screen?: string;
    };

export type DealertrackServiceTransactionDetails = BaseIpwTransactionDetails &
    DealertrackServiceTransactionExtension & {
        transaction_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_SERVICE;
        transaction_details_type_version: 1;
        dealertrack_screen?: string;
    };

export type DealertrackDealTransactionDetails = Omit<BaseIpwTransactionDetails, 'tax'> &
    DealertrackDealTransactionExtension & {
        transaction_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_DEAL;
        transaction_details_type_version: 1;
        dealertrack_screen?: string;
    };

export type DealertrackLeasingTransactionDetails = Omit<BaseIpwTransactionDetails, 'tax'> &
    DealertrackLeasingTransactionExtension & {
        transaction_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_LEASING;
        transaction_details_type_version: 1;
        dealertrack_screen?: string;
    };

export type DealertrackArStatementCustomersTransactionDetails =
    BaseIpwTransactionDetails & {
        transaction_details_type_id:
            typeof TransactionDetailsTypeId.DEALERTRACK_AR_STATEMENT_CUSTOMERS;
        transaction_details_type_version: 1;
        dealertrack_screen?: string;
    };

export type DealertrackTransactionDetails =
    | DealertrackPartsTransactionDetails
    | DealertrackServiceTransactionDetails
    | DealertrackDealTransactionDetails
    | DealertrackLeasingTransactionDetails
    | DealertrackArStatementCustomersTransactionDetails;

type DealertrackTransactionDetailsFieldKey = Extract<
    | keyof DealertrackPartsTransactionDetails
    | keyof DealertrackServiceTransactionDetails
    | keyof DealertrackDealTransactionDetails
    | keyof DealertrackLeasingTransactionDetails
    | keyof DealertrackArStatementCustomersTransactionDetails,
    string
>;

export type DealertrackStringFieldDefinition = {
    key: DealertrackTransactionDetailsFieldKey;
    label: string;
    getValue: (details: TransactionDetails) => string | null | undefined;
};

const isDealertrackPartsDetails = (
    details: TransactionDetails
): details is DealertrackPartsTransactionDetails => {
    return details.transaction_details_type_id === TransactionDetailsTypeId.DEALERTRACK_PARTS;
};

const isDealertrackServiceDetails = (
    details: TransactionDetails
): details is DealertrackServiceTransactionDetails => {
    return details.transaction_details_type_id === TransactionDetailsTypeId.DEALERTRACK_SERVICE;
};

const isDealertrackDealDetails = (
    details: TransactionDetails
): details is DealertrackDealTransactionDetails => {
    return details.transaction_details_type_id === TransactionDetailsTypeId.DEALERTRACK_DEAL;
};

const isDealertrackLeasingDetails = (
    details: TransactionDetails
): details is DealertrackLeasingTransactionDetails => {
    return details.transaction_details_type_id === TransactionDetailsTypeId.DEALERTRACK_LEASING;
};

const dealertrackPartsFields: ReadonlyArray<DealertrackStringFieldDefinition> = [
    {
        key: 'customerName',
        label: 'Customer',
        getValue: (details) =>
            isDealertrackPartsDetails(details) ? details.customerName : undefined,
    },
    {
        key: 'partsInvoiceNumber',
        label: 'Invoice #',
        getValue: (details) =>
            isDealertrackPartsDetails(details) ? details.partsInvoiceNumber : undefined,
    },
    {
        key: 'mainPhone',
        label: 'Phone',
        getValue: (details) => (isDealertrackPartsDetails(details) ? details.mainPhone : undefined),
    },
    {
        key: 'mainEmail',
        label: 'Email',
        getValue: (details) => (isDealertrackPartsDetails(details) ? details.mainEmail : undefined),
    },
];

const dealertrackServiceFields: ReadonlyArray<DealertrackStringFieldDefinition> = [
    {
        key: 'customerName',
        label: 'Customer',
        getValue: (details) =>
            isDealertrackServiceDetails(details) ? details.customerName : undefined,
    },
    {
        key: 'roNumber',
        label: 'RO #',
        getValue: (details) =>
            isDealertrackServiceDetails(details) ? details.roNumber : undefined,
    },
    {
        key: 'mainPhone',
        label: 'Phone',
        getValue: (details) =>
            isDealertrackServiceDetails(details) ? details.mainPhone : undefined,
    },
    {
        key: 'mainEmail',
        label: 'Email',
        getValue: (details) =>
            isDealertrackServiceDetails(details) ? details.mainEmail : undefined,
    },
];

const dealertrackDealFields: ReadonlyArray<DealertrackStringFieldDefinition> = [
    {
        key: 'customerName',
        label: 'Customer',
        getValue: (details) =>
            isDealertrackDealDetails(details) ? details.customerName : undefined,
    },
    {
        key: 'stockNumber',
        label: 'Stock #',
        getValue: (details) =>
            isDealertrackDealDetails(details) ? details.stockNumber : undefined,
    },
    {
        key: 'cellPhone',
        label: 'Cell Phone',
        getValue: (details) => (isDealertrackDealDetails(details) ? details.cellPhone : undefined),
    },
    {
        key: 'homePhone',
        label: 'Home Phone',
        getValue: (details) => (isDealertrackDealDetails(details) ? details.homePhone : undefined),
    },
    {
        key: 'email',
        label: 'Email',
        getValue: (details) => (isDealertrackDealDetails(details) ? details.email : undefined),
    },
];

const dealertrackLeasingFields: ReadonlyArray<DealertrackStringFieldDefinition> = [
    {
        key: 'customerName',
        label: 'Customer',
        getValue: (details) =>
            isDealertrackLeasingDetails(details) ? details.customerName : undefined,
    },
    {
        key: 'stockNumber',
        label: 'Stock #',
        getValue: (details) =>
            isDealertrackLeasingDetails(details) ? details.stockNumber : undefined,
    },
    {
        key: 'cellPhone',
        label: 'Cell Phone',
        getValue: (details) =>
            isDealertrackLeasingDetails(details) ? details.cellPhone : undefined,
    },
    {
        key: 'homePhone',
        label: 'Home Phone',
        getValue: (details) =>
            isDealertrackLeasingDetails(details) ? details.homePhone : undefined,
    },
    {
        key: 'email',
        label: 'Email',
        getValue: (details) =>
            isDealertrackLeasingDetails(details) ? details.email : undefined,
    },
];

export const getDealertrackTransactionDetailsStringFields = (
    details: TransactionDetails
): ReadonlyArray<DealertrackStringFieldDefinition> => {
    if (isDealertrackPartsDetails(details)) {
        return dealertrackPartsFields;
    }
    if (isDealertrackServiceDetails(details)) {
        return dealertrackServiceFields;
    }
    if (isDealertrackDealDetails(details)) {
        const screenParseResult = DealertrackScreenIdSchema.safeParse(details.dealertrack_screen);
        if (screenParseResult.success && screenParseResult.data === 'DEAL_BUSINESS_OFFICE') {
            return dealertrackDealFields;
        }
        return [];
    }
    if (isDealertrackLeasingDetails(details)) {
        const screenParseResult = DealertrackScreenIdSchema.safeParse(details.dealertrack_screen);
        if (screenParseResult.success && screenParseResult.data === 'LEASING') {
            return dealertrackLeasingFields;
        }
        return [];
    }
    return [];
};

export const dealertrackTransactionDetailsFieldProvider = {
    getStringFields: getDealertrackTransactionDetailsStringFields,
    getEditableStringFields: () => [],
};
